import React from 'react';
import {
  IChoiceDegreeFinder,
  IDegreeFinderPageSettings,
} from '../../../models/choice-degree-finder/IChoiceDegreeFinder';
import ChoiceDegreeFinder from '../choice-degree-finder/choice-degree-finder';
import { IDropdown } from '../../../models/dropdown/IDropdown';
import Dropdown from '../../atoms/dropdown/dropdown';
import styles from './choice-degree-finder-list.module.scss';
import Markdown from 'markdown-to-jsx';
import { ButtonStyleType } from '../../../constants/button-style-type';
import { ButtonSizeType } from '../../../constants/button-size-type';
import Button from '../../atoms/button/button';
import colorStyle from '../../../styles/utils/variables.module.scss';
import { replaceAttrSpaceWithHyphen } from '../../../utils/string-helpers/replace-attr-space-with-hyphen';

type Props = {
  choiceDegrees: IChoiceDegreeFinder[];
  programTypeDropdownValues: IDropdown[];
  affiliatedUniversitiesDropdownValues: IDropdown[];
  onSearchInputChange: (value: string) => void;
  onProgramTypeChange: (value: string) => void;
  onAffiliatedUniversityChange: (value: string) => void;
  searchInputValue: string;
  selectedProgramType: string;
  selectedAffiliatedUniversity: string;
  pageHeading: string;
  pageCopyText: string;
  isResetDisabled: boolean;
  onResetFacetsChange: () => void;
  degreeFinderPageSettings: IDegreeFinderPageSettings;
};

export default function ChoiceDegreeFinderList({
  choiceDegrees,
  programTypeDropdownValues,
  affiliatedUniversitiesDropdownValues,
  onSearchInputChange,
  onProgramTypeChange,
  onAffiliatedUniversityChange,
  searchInputValue,
  selectedProgramType,
  selectedAffiliatedUniversity,
  pageHeading,
  pageCopyText,
  isResetDisabled,
  onResetFacetsChange,
  degreeFinderPageSettings,
}: Props) {
  const handleInputChange =
    (handler: (value: string) => void) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      handler(e.target.value);
    };

  const handleDropdownChange = (handler: (value: string) => void) => (value: string) => {
    handler(value === 'all' ? '' : value);
  };

  return (
    <div>
      <div className={styles['sticky-section']}>
        <h2>{pageHeading}</h2>
        {!!pageCopyText && (
          <div className='mb-4'>
            <Markdown>{pageCopyText}</Markdown>
          </div>
        )}
        <div className='row'>
          <div className='col-xl-4 col-lg-12 mb-3'>
            <input
              type='search'
              className={styles['txtBox']}
              onChange={handleInputChange(onSearchInputChange)}
              value={searchInputValue}
              placeholder='Start typing degree name'
              aria-label='start typing here to search for your desired degree'
            />
          </div>
          <div className='col-xl-3 col-lg-5 col-md-5 col-6 mb-3'>
            {affiliatedUniversitiesDropdownValues.length > 0 && (
              <Dropdown
                ariaLabel='Dropdown List for Affiliated Universities'
                onChange={handleDropdownChange(onAffiliatedUniversityChange)}
                options={affiliatedUniversitiesDropdownValues}
                value={selectedAffiliatedUniversity}
                id='affiliated-universities-list'
              />
            )}
          </div>
          <div className='col-xl-3 col-lg-5 col-md-5 col-6 mb-3'>
            {programTypeDropdownValues.length > 0 && (
              <Dropdown
                ariaLabel='Dropdown List for Choice Program Group Names'
                onChange={handleDropdownChange(onProgramTypeChange)}
                options={programTypeDropdownValues}
                value={selectedProgramType}
                id='choice-program-group-names'
              />
            )}
          </div>
          <div
            className={`col-xl-2 col-lg-2 col-md-2 mb-0 mb-md-3 text-end text-md-start align-content-center ${styles['reset-degree-facets']}`}
          >
            <Button
              buttonID='resetDegreeFacets'
              buttonAriaLabel='reset degrees facets'
              buttonName='resetDegreeFacets'
              disabled={isResetDisabled}
              buttonStyle={ButtonStyleType.Outline}
              buttonSize={ButtonSizeType.Medium}
              buttonRole='button'
              copyColor={degreeFinderPageSettings.applicationPrimaryColor}
              outlineColor={degreeFinderPageSettings.applicationPrimaryColor}
              backgroundColor={colorStyle.white}
              hoverBackgroundColor={degreeFinderPageSettings.hoverBackgroundColor}
              activeBackgroundColor={degreeFinderPageSettings.activeBackgroundColor}
              onClick={onResetFacetsChange}
            >
              Clear all
            </Button>
          </div>
        </div>
      </div>
      {choiceDegrees?.length > 0 ? (
        choiceDegrees.map((choiceDegree) => (
          <React.Fragment
            key={`${choiceDegree.productProgressionId}-${replaceAttrSpaceWithHyphen(choiceDegree.choiceBrandProgramName)}`}
          >
            <p className={styles['row-border']}></p>
            <ChoiceDegreeFinder choiceDegree={choiceDegree} />
          </React.Fragment>
        ))
      ) : (
        <div>
          <p>No result found, please try again.</p>
        </div>
      )}
    </div>
  );
}
